<template>
	<mgl-vector-source
		v-if="tegolaTiles.length > 0"
		source-id="tegola-parcels"
		:tiles="tegolaTiles"
	>
		<mgl-fill-layer
			:before="before"
			:layer-id="layerId"
			source-layer="parcel"
			:minzoom="14"
			:paint="{
				'fill-color': '#22c55e',
				'fill-opacity': 0.06,
			}"
		/>
		<mgl-line-layer
			:before="layerId"
			layer-id="parcel-line"
			source-layer="parcel"
			:minzoom="14"
			:paint="{
				'line-color': '#22c55e',
				'line-width': 1,
				'line-opacity': 0.6,
			}"
		/>
		<!-- Selection highlight for the parcel of a selected site or situs
		     result: the same prominent solid cyan outline as the pool
		     selection, keyed on the parcel's assessor id (external_id). The
		     layer is always present so the filter (not a v-if mount) drives
		     the highlight — it never matches when nothing is selected. -->
		<mgl-line-layer
			:before="layerId"
			layer-id="parcel-selected-outline"
			source-layer="parcel"
			:minzoom="14"
			:filter="parcelSelectedFilter"
			:paint="{
				'line-color': '#00e5ff',
				'line-width': 3,
				'line-opacity': 1,
			}"
		/>
	</mgl-vector-source>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { MglVectorSource, MglFillLayer, MglLineLayer } from "vue-maplibre-gl";
import { useSessionStore } from "@/store/session";

interface Props {
	before?: string;
	layerId?: string;
	/** The assessor parcel id (municipal.parcel.external_id) to highlight. */
	selectedParcelExternalId?: string | null;
}
const props = withDefaults(defineProps<Props>(), {
	layerId: "parcel-fill",
	selectedParcelExternalId: null,
});
const session = useSessionStore();

const tegolaTiles = computed(() => {
	const base = session.urls?.tegola ?? "";
	if (!base) return [];
	return [`${base}maps/nidus/{z}/{x}/{y}`];
});

// Matches the selected parcel's assessor id (the parcel MVT carries the
// external_id property). The never-match fallback keeps the expression valid
// when nothing is selected — the always-present layer's filter simply never
// matches.
// vue-maplibre-gl's filter prop is an opaque generated union; `any` is
// assignable to any member of it.
const parcelSelectedFilter = computed(() => {
	const expr: unknown[] = props.selectedParcelExternalId
		? ["==", ["get", "external_id"], props.selectedParcelExternalId]
		: (["==", ["get", "external_id"], ""] as unknown[]);
	return expr as unknown as any;
});
</script>
